import { useMemo } from 'react'
import { lastIndexOf } from 'ramda'
import { CallType } from '@weblite/ts-action/media-proxy'
import { useActiveSectionsHistory, useActiveSectionsTop } from 'weblite-router'
import { usePrevious } from '$common/react-hooks/use-previous'
import { useCallStorage } from '$logic/call'

function shouldCallBeOnChatList(callType?: CallType) {
  return callType === 'tribune' || callType === 'live'
}

export function useCommSectionVisibilityStateInDesktopSize(): {
  show: boolean
  fullscreen?: boolean
} {
  const activeSectionsHistory = useActiveSectionsHistory()

  const activeSection = useActiveSectionsTop()
  const prevActiveSection = usePrevious(activeSection)

  const { callType, pvMeta, roomId: ongoingCallRoomId } = useCallStorage()

  const prevCallType = usePrevious(callType, true)

  return useMemo(() => {
    if (prevCallType === 'call' && !pvMeta.roomId && !ongoingCallRoomId)
      return { show: false }

    const commSectionIsOnTop =
      activeSection === 'comm' ||
      (prevActiveSection === 'comm' && activeSection === 'overlay')

    const showOnChatList =
      shouldCallBeOnChatList(callType) ||
      // For calls that have ended page.
      (!callType && shouldCallBeOnChatList(prevCallType))

    // 1- If commSection is fullscreen, behave as before
    if (!showOnChatList)
      return {
        fullscreen: true,
        show: commSectionIsOnTop,
      }

    // 2- Otherwise, in some scenarios, comm section should be shown even if it's not at the top of active sections

    // 2- (I) If comm section is on top of activeSectionsHistory, show it
    if (commSectionIsOnTop) return { show: true, fullscreen: false }

    // 2- (II) Otherwise, compare its index with left section and show the one which has higher index (is topper!)
    const commSectionLastIndex = lastIndexOf('comm', activeSectionsHistory)
    const leftSectionLastIndex = lastIndexOf('left', activeSectionsHistory)

    return {
      fullscreen: false,
      show:
        commSectionLastIndex == undefined
          ? false
          : leftSectionLastIndex == undefined
            ? true
            : commSectionLastIndex > leftSectionLastIndex,
    }
  }, [
    activeSection,
    activeSectionsHistory,
    prevActiveSection,
    pvMeta,
    callType,
    prevCallType,
    ongoingCallRoomId,
  ])
}
